import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Snackbar,
  Alert,
  Box,
  InputAdornment,
  IconButton,
} from "@mui/material";
import useAxiosConfig from "../config/AxiosConfig";
import WeekDayPicker from "./WeekDayPicker";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { FiUpload } from "react-icons/fi";

const amenitiesList = [
  { key: "floodLights", label: "Flood lights" },
  { key: "seatingArea", label: "Seating area" },
  { key: "securityCameras", label: "Cameras" },
  { key: "bibs", label: "Sports Equipments" },
  { key: "scoreBoard", label: "Electronic score board" },
  { key: "parkingArea", label: "Parking area" },
  { key: "restRooms", label: "Rest rooms" },
  { key: "footballs", label: "Footballs" },
];

const sportsNames = [
  "Cricket",
  "Football",
  "Padel",
  "Volleyball",
  "Pickleball",
  "Basketball",
  "Cricket Net",
  "All Sports",
];
const formats = ["5V5", "6V6", "7V7", "8V8", "9V9", "10V10", "11V11"];

const durations = ["60 min", "90 min", "120 min"];

const AddGroundModal = ({
  showModal,
  handleClose,
  handleSave,
  newGroundData,
  setNewGroundData,
  managersName,
  setManagersName,
}) => {
  const axiosInstance = useAxiosConfig();

  // Basic ground data
  const [data, setData] = useState({
    name: "",
    managedBy: "",
    location: "",
    advancePayment: "",
    images: [],
  });

  // Coordinates
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);

  // Multiple times and timing
  const [switchStatus, setSwitchStatus] = useState(true);
  const [timingData, setTimingData] = useState([
    { startTime: "", endTime: "", peakTime: "", StartDays: [] },
    { startTime: "", endTime: "", peakTime: "", StartDays: [] },
  ]);

  // Fields management
  const [fieldsValue, setFieldsValue] = useState(1);

  const [subFieldsPricing, setSubFieldsPricing] = useState([
    {
      allowedSports: [],
      length: "",
      width: "",
      height: "",
      selectedDurations: [], // Track selected durations for this field
      pricing: {
        "60 min": { offPeakPrice: "", peakPrice: "" },
        "90 min": { offPeakPrice: "", peakPrice: "" },
        "120 min": { offPeakPrice: "", peakPrice: "" },
      },
      noOfSubfields: "",
      pricings: [],
    },
  ]);

  // Amenities and images
  const [selectedAmenities, setSelectedAmenities] = useState({
    floodLights: false,
    restRooms: false,
    parkingArea: false,
    securityCameras: false,
    seatingArea: false,
    scoreBoard: false,
    bibs: false,
    footballs: false,
  });

  const [featureImage, setFeatureImage] = useState(null);
  const [otherImage, setOtherImage] = useState(null);
  const [addStatus, setAddStatus] = useState(null);
  const [subFields, setSubFields] = useState([]);
  const [nextId, setNextId] = useState(1);
  const [showSubField, setShowSubField] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedDurations, setSelectedDurations] = useState([]);

  const [fields, setFields] = useState([
    {
      allowedSports: [],
      length: "",
      width: "",
      height: "",
      pricing: [],
      matchType: "",
      noOfSubfields: "",
      footballSelected: false,
    },
  ]);

  useEffect(() => {
    console.log("Testing this one : ", fieldsValue, fields);
  }, [fieldsValue, fields]);

  // Get managers on component mount
  useEffect(() => {
    const getManagers = async () => {
      try {
        const response = await axiosInstance.get("api/ground/managers");
        setManagersName(response?.data?.managers);
      } catch (error) {
        console.error("Error getting managers:", error);
      }
    };
    getManagers();
  }, [setManagersName]);

  // Handle image upload
  const handleImageUpload = async (e) => {
    const imageFile = e.target.files[0];
    if (!imageFile) return;

    try {
      const formData = new FormData();
      formData.append("img", imageFile);

      const response = await axiosInstance.post("api/upload/image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const imageUrl = response.data.img_url;
      setData((prevData) => ({
        ...prevData,
        images: [...prevData.images, imageUrl],
      }));

      setAddStatus({ message: "Image uploaded successfully", success: true });
    } catch (error) {
      console.error("Error uploading image:", error);
      setAddStatus({ message: "Failed to upload image", success: false });
    }
  };

  // Handle form submission
  const handleAddClick = async () => {
    try {
      const coordinate = [parseFloat(longitude), parseFloat(latitude)];
      const loc = { type: "Point", coordinates: coordinate };
      const multipleSelection = switchStatus ? false : true;

      const submissionData = {
        ...data,
        multipleTimes: multipleSelection,
        loc,
        timing: { ...timingData },
        properties: { ...selectedAmenities },
        fields: fields, // Include the fields data
      };

      console.log("Submission data:", submissionData);

      const response = await axiosInstance.post(
        "api/admin/createGround",
        submissionData
      );
      handleSave();
      setAddStatus({ message: "Ground added successfully", success: true });
    } catch (error) {
      console.error("Error adding ground:", error);
      setAddStatus({
        message: "Failed to add ground, some fields are empty",
        success: false,
      });
    }
  };

  // Handle basic input changes
  const handleInputChange = (e) => {
    setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Handle timing changes
  const handleTimingChange = (e, index) => {
    const { name, value } = e.target;
    setTimingData((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [name]: value } : item))
    );
  };

  // Handle day selection
  const handleDaySelection = (newDays, index) => {
    setTimingData((prev) =>
      prev.map((item, i) => {
        if (i === index) {
          const currentArray = item.StartDays || [];
          const updatedArray = currentArray.includes(newDays)
            ? currentArray
            : [...currentArray, newDays];
          return { ...item, StartDays: updatedArray };
        }
        return item;
      })
    );
  };

  // Handle multiple times status
  const handleStatusChange = (value) => {
    setSwitchStatus(value !== "enable");
  };

  // Handle number of fields change
  // const handleFields = (e) => {
  //   const newValue = parseInt(e.target.value) || 1;
  //   setFieldsValue(newValue);

  //   // Adjust fields array to match the new count
  //   setFields((prev) => {
  //     const newFields = [...prev];

  //     if (newValue > prev.length) {
  //       // Add new fields
  //       for (let i = prev.length; i < newValue; i++) {
  //         newFields.push({
  //           allowedSports: [],
  //           length: "",
  //           width: "",
  //           height: "",
  //           selectedDurations: [],
  //           pricing: {
  //             "60 min": { offPeakPrice: "", peakPrice: "" },
  //             "90 min": { offPeakPrice: "", peakPrice: "" },
  //             "120 min": { offPeakPrice: "", peakPrice: "" },
  //           },
  //         });
  //       }
  //     } else if (newValue < prev.length) {
  //       // Remove excess fields
  //       newFields.splice(newValue);
  //     }

  //     return newFields;
  //   });
  // };

  // Handle field property changes
  const handleChangeFields = (e, fieldIndex) => {
    const { name, value } = e.target;
    setFields((prev) =>
      prev.map((field, i) =>
        i === fieldIndex ? { ...field, [name]: value } : field
      )
    );
  };

  // Handle sports selection
  const handleSportsValues = (sports, fieldIndex) => {
    console.log("This one im testing ", sports, fieldIndex);
    // if (sports === "Football") {
    //   console.log("Im inside the if ");
    //   setShowSubField(!showSubField);
    // }
    if (sports === "Football") {
      setFields((prev) => {
        return prev.map((fields, i) =>
          i === fieldIndex
            ? {
                ...fields,
                footballSelected: !fields.footballSelected,
              }
            : fields
        );
      });
    }

    setFields((prev) =>
      prev.map((field, i) => {
        if (i === fieldIndex) {
          const isAlreadySelected = field.allowedSports.includes(sports);
          return {
            ...field,
            allowedSports: isAlreadySelected
              ? field.allowedSports.filter((sport) => sport !== sports)
              : [...field.allowedSports, sports],
          };
        }
        return field;
      })
    );
  };

  useEffect(() => {
    console.log("selected Duration are", selectedDurations);
  }, [selectedDurations]);

  // Handle duration selection for specific field
  const handleDurationSelection = (duration, fieldIndex) => {
    console.log("duration is ", duration, fieldIndex);
    // setSelectedDurations((prev) =>
    //   prev.map((field, i) => {
    //     if (i === fieldIndex) {
    //       const isSelected = field.includes(duration);
    //       return {
    //         ...field,
    //         selectedDurations: isSelected
    //           ? field.filter((d) => d !== duration)
    //           : [...field, duration].sort((a, b) => {
    //               // Sort durations in order: 60 min, 90 min, 120 min
    //               const order = ["60 min", "90 min", "120 min"];
    //               return order.indexOf(a) - order.indexOf(b);
    //             }),
    //       };
    //     }
    //     return field;
    //   })
    // );

    // setSelectedDurations((prev) => {
    //   // return [...prev, duration];
    //   // return prev.filter((item, index) => (item !== duration ? item : null));
    //   // const isSelected = prev.includes(duration)
    //   // console.log("checkingggggg", isSelected)
    //   // return  [...prev, duration]
    //   return prev.map((item, index) =>
    //     item.includes(duration) ? item : duration
    //   );
    // });
    setFields((prev) => {
      return prev.map((field, index) => {
        if (fieldIndex === index) {
          const alreadyExist = field.pricing.some(
            (item) => item.duration === duration
          );
          const UpdatedPricing = alreadyExist
            ? field.pricing.filter((item) => item.duration !== duration)
            : [...field.pricing, { duration }];
          return {
            ...field,
            pricing: UpdatedPricing,
          };
        }
      });
    });
  };

  // Handle pricing changes
  const handlePricingChange = (e, fieldIndex, duration, priceType) => {
    const { value } = e.target;
    setFields((prev) =>
      prev.map((field, i) => {
        if (i === fieldIndex) {
          return {
            ...field,
            pricing: {
              ...field.pricing,
              [duration]: {
                ...field.pricing[duration],
                [priceType]: value,
              },
            },
          };
        }
        return field;
      })
    );
  };

  // Handle amenities toggle
  const handleToggle = (amenity) => {
    setSelectedAmenities((prev) => ({
      ...prev,
      [amenity]: !prev[amenity],
    }));
  };

  useEffect(() => {
    console.log("testinggg123", subFields);
  }, [subFields]);

  const handleAddIcon = () => {
    setSubFields((prev) => {
      return [...prev, { id: nextId, call: "hello" }];
    });
    setNextId((prev) => prev + 1);
  };

  const handleDeleteSubFields = (id) => {
    setSubFields((prev) => {
      return prev.filter((item) => item.id !== id);
    });
  };
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  useEffect(() => {
    console.log("Running");
  });

  const handleFields = (e) => {
    setFieldsValue(e.target.value);
    setFields((prev) => {
      return [
        ...prev,
        {
          allowedSports: [],
          length: "",
          width: "",
          height: "",
          selectedDurations: [], // Track selected durations for this field
          pricing: [],
          matchType: "",
        },
      ];
    });
    console.log("runned till the end");
  };

  const handleMatchType = (format, fieldIndex) => {
    console.log("checking matchTypes :", format, fieldIndex);
    setFields((prev) => {
      return prev.map((item, i) =>
        fieldIndex === i
          ? { ...item, matchType: item.matchType !== format ? format : "" }
          : item
      );
    });
  };

  return (
    <>
      <Dialog
        open={showModal}
        onClose={handleClose}
        maxWidth="lg"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <DialogTitle>Add New Ground</DialogTitle>
        <DialogContent>
          <form>
            {/* Basic Information */}
            <TextField
              required
              name="name"
              label="Ground Name"
              variant="outlined"
              value={data.name}
              onChange={handleInputChange}
              sx={{
                "& label.Mui-focused": { color: "#0C7240" },
                "& .MuiInput-underline:after": { borderBottomColor: "#0C7240" },
                margin: "10px",
              }}
            />

            <TextField
              required
              name="managedBy"
              label="Manager Name"
              variant="outlined"
              value={data.managedBy}
              onChange={handleInputChange}
              sx={{
                "& label.Mui-focused": { color: "#0C7240" },
                "& .MuiInput-underline:after": { borderBottomColor: "#0C7240" },
                margin: "10px",
              }}
            />

            <TextField
              required
              name="location"
              label="Location"
              variant="outlined"
              value={data.location}
              onChange={handleInputChange}
              sx={{
                "& label.Mui-focused": { color: "#0C7240" },
                "& .MuiInput-underline:after": { borderBottomColor: "#0C7240" },
                margin: "10px",
              }}
            />

            {/* Multiple Times Section */}
            <h3 className="text-lg font-bold">Multiple Times</h3>
            <div className="my-4">
              <label className="mr-8 ml-2">
                <input
                  type="radio"
                  name="status"
                  value="enable"
                  onChange={(e) => handleStatusChange(e.target.value)}
                />{" "}
                Enable
              </label>
              <label>
                <input
                  type="radio"
                  name="status"
                  value="disable"
                  onChange={(e) => handleStatusChange(e.target.value)}
                />{" "}
                Disable
              </label>
            </div>

            {/* Timing Section */}
            <div className="mt-4">
              {switchStatus ? (
                // Single time slot
                <>
                  <h3 className="text-md font-semibold">Time Slot</h3>
                  <TextField
                    required
                    name="openingTime"
                    label="Opening Time"
                    variant="outlined"
                    onChange={handleInputChange}
                    sx={{
                      "& label.Mui-focused": { color: "#0C7240" },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#0C7240",
                      },
                      margin: "10px",
                    }}
                  />
                  <TextField
                    required
                    name="closingTime"
                    label="Closing Time"
                    variant="outlined"
                    onChange={handleInputChange}
                    sx={{
                      "& label.Mui-focused": { color: "#0C7240" },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#0C7240",
                      },
                      margin: "10px",
                    }}
                  />
                  <TextField
                    required
                    name="peakTime"
                    label="Peak Time"
                    variant="outlined"
                    onChange={handleInputChange}
                    sx={{
                      "& label.Mui-focused": { color: "#0C7240" },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#0C7240",
                      },
                      margin: "10px",
                    }}
                  />
                </>
              ) : (
                // Multiple time slots
                <>
                  <h3 className="text-md font-semibold">Time Slot 1</h3>
                  <TextField
                    required
                    name="startTime"
                    label="Opening Time 1"
                    variant="outlined"
                    value={timingData[0].startTime}
                    onChange={(e) => handleTimingChange(e, 0)}
                    sx={{
                      "& label.Mui-focused": { color: "#0C7240" },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#0C7240",
                      },
                      margin: "10px",
                    }}
                  />
                  <TextField
                    required
                    name="endTime"
                    label="Closing Time 1"
                    variant="outlined"
                    value={timingData[0].endTime}
                    onChange={(e) => handleTimingChange(e, 0)}
                    sx={{
                      "& label.Mui-focused": { color: "#0C7240" },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#0C7240",
                      },
                      margin: "10px",
                    }}
                  />
                  <TextField
                    required
                    name="peakTime"
                    label="Peak Time 1"
                    variant="outlined"
                    value={timingData[0].peakTime}
                    onChange={(e) => handleTimingChange(e, 0)}
                    sx={{
                      "& label.Mui-focused": { color: "#0C7240" },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#0C7240",
                      },
                      margin: "10px",
                    }}
                  />
                  <WeekDayPicker
                    title={"Weekdays 1"}
                    selectedDays={timingData[0].StartDays}
                    handleDaySelection={(days) => handleDaySelection(days, 0)}
                  />

                  <br />
                  <h3 className="text-md font-semibold">Time Slot 2</h3>
                  <TextField
                    required
                    name="startTime"
                    label="Opening Time 2"
                    variant="outlined"
                    value={timingData[1].startTime}
                    onChange={(e) => handleTimingChange(e, 1)}
                    sx={{
                      "& label.Mui-focused": { color: "#0C7240" },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#0C7240",
                      },
                      margin: "10px",
                    }}
                  />
                  <TextField
                    required
                    name="endTime"
                    label="Closing Time 2"
                    variant="outlined"
                    value={timingData[1].endTime}
                    onChange={(e) => handleTimingChange(e, 1)}
                    sx={{
                      "& label.Mui-focused": { color: "#0C7240" },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#0C7240",
                      },
                      margin: "10px",
                    }}
                  />
                  <TextField
                    required
                    name="peakTime"
                    label="Peak Time 2"
                    variant="outlined"
                    value={timingData[1].peakTime}
                    onChange={(e) => handleTimingChange(e, 1)}
                    sx={{
                      "& label.Mui-focused": { color: "#0C7240" },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#0C7240",
                      },
                      margin: "10px",
                    }}
                  />
                  <WeekDayPicker
                    title={"Weekdays 2"}
                    selectedDays={timingData[1].StartDays}
                    handleDaySelection={(days) => handleDaySelection(days, 1)}
                  />
                </>
              )}
            </div>

            {/* Fields Configuration */}
            <div className="mt-8 mb-2">
              <TextField
                required
                type="number"
                name="numberOfFields"
                label="Number of fields"
                variant="outlined"
                value={fieldsValue}
                onChange={handleFields}
                sx={{
                  "& label.Mui-focused": { color: "#0C7240" },
                  "& .MuiInput-underline:after": {
                    borderBottomColor: "#0C7240",
                  },
                  margin: "10px",
                }}
              />

              <TextField
                name="advancePayment"
                label="Advance Payment %"
                variant="outlined"
                type="number"
                value={data.advancePayment}
                onChange={handleInputChange}
                sx={{
                  "& label.Mui-focused": { color: "#0C7240" },
                  "& .MuiInput-underline:after": {
                    borderBottomColor: "#0C7240",
                  },
                  margin: "10px",
                }}
              />
            </div>

            {/* Field Details */}
            {fields.map((field, fieldIndex) => (
              <div key={fieldIndex}>
                <h2 className="font-bold text-lg mb-2">
                  Field {fieldIndex + 1} Details
                </h2>

                {/* Sport Types */}
                <div className="font-bold text-lg mb-3">Sport Type</div>
                <div className="grid grid-cols-3 gap-2">
                  {sportsNames.map((sport, i) => (
                    <label key={i} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={field.allowedSports.includes(sport)}
                        onChange={() => handleSportsValues(sport, fieldIndex)}
                      />
                      <span>{sport}</span>
                    </label>
                  ))}
                </div>

                {field.footballSelected ? (
                  <>
                    <div className="font-bold text-lg mt-4 mb-3">
                      Match Type
                    </div>

                    <div className="flex gap-6 ">
                      {formats.map((format, index) => (
                        <span className="flex gap-2">
                          <input
                            type="radio"
                            checked={fields[fieldIndex].matchType === format}
                            onChange={() => handleMatchType(format, fieldIndex)}
                          />
                          <span>{format}</span>
                        </span>
                      ))}
                    </div>
                  </>
                ) : null}

                {/* Dimensions */}
                <div className="font-bold text-lg mt-4 mb-2">Dimensions</div>
                <div className="flex">
                  <div>
                    <h3 className="font-semibold ml-2">Length</h3>
                    <TextField
                      name="length"
                      variant="outlined"
                      type="text"
                      value={field.length}
                      onChange={(e) => handleChangeFields(e, fieldIndex)}
                      size="small"
                      sx={{
                        "& label.Mui-focused": { color: "#0C7240" },
                        "& .MuiInput-underline:after": {
                          borderBottomColor: "#0C7240",
                        },
                        margin: "10px",
                      }}
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold ml-2">Width</h3>
                    <TextField
                      name="width"
                      variant="outlined"
                      type="number"
                      value={field.width}
                      onChange={(e) => handleChangeFields(e, fieldIndex)}
                      size="small"
                      sx={{
                        "& label.Mui-focused": { color: "#0C7240" },
                        "& .MuiInput-underline:after": {
                          borderBottomColor: "#0C7240",
                        },
                        margin: "10px",
                      }}
                    />
                  </div>
                </div>

                <div className="mt-2">
                  <h3 className="font-semibold ml-2">Height (Optional)</h3>
                  <TextField
                    name="height"
                    variant="outlined"
                    type="number"
                    value={field.height}
                    onChange={(e) => handleChangeFields(e, fieldIndex)}
                    size="small"
                    sx={{
                      "& label.Mui-focused": { color: "#0C7240" },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#0C7240",
                      },
                      margin: "10px",
                    }}
                  />
                </div>

                {/* Duration Selection */}
                <h3 className="font-bold text-lg mt-4 mb-2">Duration</h3>
                <div className="flex gap-20">
                  {durations.map((duration, i) => (
                    <div
                      className="flex gap-2 items-center justify-center"
                      key={i}
                    >
                      <input
                        type="checkbox"
                        checked={field.pricing?.duration?.includes(duration)}
                        onChange={() =>
                          handleDurationSelection(duration, fieldIndex)
                        }
                      />
                      {duration}
                    </div>
                  ))}
                </div>

                {/* Pricing Information */}
                {field.pricing?.length > 0 && (
                  <>
                    <h3 className="font-bold text-lg mt-4 mb-2">
                      Pricing Information
                    </h3>
                    {field.pricing.map((duration, durationIndex) => (
                      <div key={durationIndex}>
                        <p className="font-bold text-lg mt-4 mb-2">
                          {duration.duration}
                        </p>
                        <div className="flex">
                          <div>
                            <p className="font-semibold text-lg mt-4 mb-2 ml-3">
                              Off Peak Hours
                            </p>
                            <TextField
                              variant="outlined"
                              placeholder="4000"
                              type="number"
                              // value={field.pricing[duration].offPeakPrice}
                              onChange={(e) =>
                                handlePricingChange(
                                  e,
                                  fieldIndex,
                                  duration,
                                  "offPeakPrice"
                                )
                              }
                              size="small"
                              sx={{
                                "& label.Mui-focused": { color: "#0C7240" },
                                "& .MuiInput-underline:after": {
                                  borderBottomColor: "#0C7240",
                                },
                                margin: "10px",
                              }}
                            />
                          </div>
                          <div>
                            <p className="font-semibold text-lg mt-4 mb-2 ml-3">
                              Peak Hours
                            </p>
                            <TextField
                              variant="outlined"
                              placeholder="5000"
                              type="number"
                              // value={field.pricing[duration].peakPrice}
                              onChange={(e) =>
                                handlePricingChange(
                                  e,
                                  fieldIndex,
                                  duration,
                                  "peakPrice"
                                )
                              }
                              size="small"
                              sx={{
                                "& label.Mui-focused": { color: "#0C7240" },
                                "& .MuiInput-underline:after": {
                                  borderBottomColor: "#0C7240",
                                },
                                margin: "10px",
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {field.footballSelected && (
                  <>
                    {() => console.log("This is field Index ", fieldIndex)}
                    <div className="flex gap-6 mt-4">
                      <h2 className="font-bold text-lg mb-3">Sub Fields</h2>
                      <AddIcon onClick={handleAddIcon} />
                    </div>

                    {subFields.length > 0 &&
                      subFields.map((item, index) => (
                        <div key={index}>
                          <TextField
                            name="length"
                            variant="outlined"
                            type="text"
                            onClick={() => setOpenModal(true)}
                            // value={field.length}
                            // onChange={(e) => handleChangeFields(e, fieldIndex)}
                            size="small"
                            sx={{
                              "& label.Mui-focused": { color: "#0C7240" },
                              "& .MuiInput-underline:after": {
                                borderBottomColor: "#0C7240",
                              },
                              margin: "10px",
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() =>
                                      handleDeleteSubFields(item.id)
                                    }
                                    size="small"
                                    sx={{ color: "#ef4444" }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </div>
                      ))}
                  </>
                )}
              </div>
            ))}

            <hr className="my-4 border-t border-gray-300" />
            {/* Live Bookings */}
            <h3 className="text-lg font-bold">Live Bookings</h3>
            <div className="my-2">
              <label className="mr-8 ml-2">
                <input type="radio" name="liveBookings" value="enable" /> Enable
              </label>
              <label>
                <input type="radio" name="liveBookings" value="disable" />{" "}
                Disable
              </label>
            </div>

            {/* Coordinates */}
            <h3 className="text-md font-semibold mt-4">Coordinates</h3>
            <div className="flex mt-2">
              <div style={{ display: "flex", flexDirection: "column" }}>
                <TextField
                  required
                  label="Longitude"
                  variant="outlined"
                  type="number"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                  sx={{
                    "& label.Mui-focused": { color: "#0C7240" },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#0C7240",
                    },
                    margin: "10px",
                  }}
                />
                {longitude > 99 && (
                  <span
                    style={{
                      marginLeft: "10px",
                      fontSize: "10px",
                      color: "red",
                    }}
                  >
                    Please enter a valid longitude between -99 and 99
                  </span>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <TextField
                  required
                  label="Latitude"
                  variant="outlined"
                  type="number"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                  sx={{
                    "& label.Mui-focused": { color: "#0C7240" },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#0C7240",
                    },
                    margin: "10px",
                  }}
                />
                {latitude > 99 && (
                  <span
                    style={{
                      marginLeft: "10px",
                      fontSize: "10px",
                      color: "red",
                    }}
                  >
                    Please enter a valid latitude between -99 and 99
                  </span>
                )}
              </div>
            </div>

            {/* Image Uploads */}
            <h3 className="text-lg font-bold mt-4">Feature Image</h3>
            <label className="flex flex-col items-center justify-center w-64 h-40 border-2 border-dashed border-gray-400 rounded-lg cursor-pointer hover:border-gray-600 transition">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
              {featureImage ? (
                <img
                  src={featureImage}
                  alt="Feature"
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <div className="flex flex-col items-center text-gray-500">
                  <FiUpload size={24} />
                  <span className="mt-2 text-sm">Upload</span>
                </div>
              )}
            </label>

            <h3 className="text-lg font-bold mt-8">Other Images (Optional)</h3>
            <label className="flex flex-col items-center justify-center w-64 h-40 border-2 border-dashed border-gray-400 rounded-lg cursor-pointer hover:border-gray-600 transition">
              <input type="file" accept="image/*" className="hidden" />
              {otherImage ? (
                <img
                  src={otherImage}
                  alt="Other"
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <div className="flex flex-col items-center text-gray-500">
                  <FiUpload size={24} />
                  <span className="mt-2 text-sm">Upload</span>
                </div>
              )}
            </label>

            {/* Amenities */}
            <h2 className="text-lg font-semibold mb-4 mt-6">
              Amenities Available
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {amenitiesList.map((amenity, index) => (
                <label key={index} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedAmenities[amenity.key]}
                    onChange={() => handleToggle(amenity.key)}
                    className="w-4 h-4 accent-green-600"
                  />
                  <span>{amenity.label}</span>
                </label>
              ))}
            </div>
          </form>
        </DialogContent>

        <DialogActions
          sx={{ justifyContent: "center", gap: "10px", padding: "10px" }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddClick}
            disabled={addStatus !== null}
          >
            {addStatus !== null ? "Adding..." : "Add Ground"}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleClose}
            style={{ marginLeft: "8px" }}
          >
            Close
          </Button>
        </DialogActions>

        {/* Status Snackbar */}
        {addStatus !== null && (
          <Box>
            <Snackbar
              open={true}
              autoHideDuration={5000}
              onClose={() => setAddStatus(null)}
            >
              <Alert
                severity={addStatus.success ? "success" : "error"}
                onClose={() => setAddStatus(null)}
              >
                {addStatus.message}
              </Alert>
            </Snackbar>
          </Box>
        )}
      </Dialog>
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="lg"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: "10px",
        }}
      >
        <DialogContent sx={{ p: 3 }}>
          <div className="font-bold text-lg mt-4 mb-3">Match Type</div>
          <div className="grid grid-cols-4 gap-2 ">
            {formats.map((format, index) => (
              <span className="flex gap-2">
                <input
                  type="radio"
                  //  checked={field.allowedSports.includes(sport)}
                  //  onChange={() => handleSportsValues(sport, fieldIndex)}
                />
                <span>{format}</span>
              </span>
            ))}
          </div>
          <h3 className="font-bold text-lg mt-4 mb-2">Duration</h3>
          <div className="flex gap-20">
            {durations.map((duration, i) => (
              <div className="flex gap-2 items-center justify-center" key={i}>
                <input
                  type="checkbox"
                  // checked={field.selectedDurations.includes(duration)}
                  // onChange={() => handleDurationSelection(duration, fieldIndex)}
                />
                {duration}
              </div>
            ))}
          </div>
          <h3 className="font-bold text-lg mt-4 mb-2">Pricing Information</h3>
          {durations.map((duration, durationIndex) => (
            <div key={durationIndex}>
              <p className="font-bold text-lg mt-4 mb-2">{duration}</p>
              <div className="flex">
                <div>
                  <p className="font-semibold text-lg mt-4 mb-2 ml-3">
                    Off Peak Hours
                  </p>
                  <TextField
                    variant="outlined"
                    placeholder="4000"
                    type="number"
                    // value={field.pricing[duration].offPeakPrice}
                    // onChange={(e) =>
                    //   handlePricingChange(
                    //     e,
                    //     fieldIndex,
                    //     duration,
                    //     "offPeakPrice"
                    //   )
                    // }
                    size="small"
                    sx={{
                      "& label.Mui-focused": { color: "#0C7240" },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#0C7240",
                      },
                      margin: "10px",
                    }}
                  />
                </div>
                <div>
                  <p className="font-semibold text-lg mt-4 mb-2 ml-3">
                    Peak Hours
                  </p>
                  <TextField
                    variant="outlined"
                    placeholder="5000"
                    type="number"
                    // value={field.pricing[duration].peakPrice}
                    // onChange={(e) =>
                    //   handlePricingChange(e, fieldIndex, duration, "peakPrice")
                    // }
                    size="small"
                    sx={{
                      "& label.Mui-focused": { color: "#0C7240" },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#0C7240",
                      },
                      margin: "10px",
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddGroundModal;
